import { createSelector } from 'reselect';
import WixSDK from 'shared/utils/wix-sdk';
import { VIEW_MODES } from '@wix/wix-vod-constants/dist/common/view-modes';
import {
  canShowCancelSubscription,
  canShowSubscription,
} from 'widget/selectors/subscription';
import { getMainVideo } from 'widget/selectors/get-video';
import {
  isVideoPlayingOptimistic,
  isVideoPausedOptimistic,
} from 'widget/selectors/video-playback-status';
import {
  canShowChannelShare,
  getCanShowChannelInfo,
  getCanShowSignIn,
  canShowChannelTitle,
  isGridLayoutMobile,
} from 'shared/selectors/app-settings';
import { getOverlayContent } from 'shared/selectors/player';
import { isPortableDevice } from 'shared/utils/device/screen';
import { VIDEO_SOURCES } from '@wix/wix-vod-constants/dist/common/video-sources';
import * as MOBILE_VIDEOS_PER_PAGE_COUNT from '@wix/wix-vod-constants/dist/mobile/videos-per-page-count';
import INFO_OVERLAY_CONTENT from 'widget/player-widget/components/new-player/containers/animated-overlay-content/info-overlay-content-enums';
import {
  GRID_HEADER_HEIGHT,
  GRID_VIDEO_HEIGHT,
  GRID_VIDEO_MARGIN_BOTTOM,
  GRID_LOAD_MORE_BUTTON_HEIGHT,
  DEFAULT_WIDGET_HEIGHT,
  DEFAULT_WIDGET_WIDTH,
} from 'widget/constants/sizes';
import {
  getChannelVideos,
  getCurrentPageIndex,
} from 'widget/redux/client/lazy-channel-videos/selectors';

export const canShowActionBar = createSelector(
  canShowChannelTitle,
  getCanShowSignIn,
  getCanShowChannelInfo,
  canShowChannelShare,
  canShowCancelSubscription,
  canShowSubscription,
  (...args) => _.some(args),
);

export const showAutoPlay = createSelector(
  getMainVideo,
  mainVideo =>
    !!mainVideo &&
    !(mainVideo.videoSource === VIDEO_SOURCES.FACEBOOK && isPortableDevice()),
);

export const isAllCardsOverlayVisible = createSelector(
  getOverlayContent,
  overlayContent => overlayContent === INFO_OVERLAY_CONTENT.ALL_CARDS,
);

export const isPlayerActive = createSelector(
  isAllCardsOverlayVisible,
  isVideoPlayingOptimistic,
  isVideoPausedOptimistic,
  (isAllCardsOverlayVisible, isVideoPlaying, isVideoPaused) =>
    isAllCardsOverlayVisible || isVideoPlaying || isVideoPaused,
);

export const getGridVisibleVideos = createSelector(
  getCurrentPageIndex,
  getChannelVideos,
  (currentPageIndex, channelVideos) =>
    _.slice(
      channelVideos,
      0,
      (currentPageIndex + 1) * MOBILE_VIDEOS_PER_PAGE_COUNT.GRID_LAYOUT,
    ),
);

export const getWidgetSize = createSelector(
  isGridLayoutMobile,
  getGridVisibleVideos,
  state => state.windowSize,
  (isGrid, gridVisibleVideos, windowSize) => {
    const isSiteMode = WixSDK.Utils.getViewMode() === VIEW_MODES.SITE;

    if (isGrid) {
      const visibleVideosCount = gridVisibleVideos.length;
      const width = DEFAULT_WIDGET_WIDTH;
      let height = DEFAULT_WIDGET_HEIGHT;

      if (visibleVideosCount > 0) {
        height =
          GRID_HEADER_HEIGHT +
          visibleVideosCount * (GRID_VIDEO_HEIGHT + GRID_VIDEO_MARGIN_BOTTOM) +
          GRID_LOAD_MORE_BUTTON_HEIGHT;
      }

      return {
        height,
        width,
      };
    }

    // Previously users had a possibility to resize widget height. Now widget calculates height by itself.
    // But some users had already developed site layouts based on old widget height.
    // So, we do not touch widget height in view mode unless user will not try to change it in edit mode,
    // then widget will recalculate height.
    if (isSiteMode) {
      const { height, width } = windowSize;
      return {
        height: Math.max(height, DEFAULT_WIDGET_HEIGHT),
        width,
      };
    }

    return {
      width: DEFAULT_WIDGET_WIDTH,
      height: DEFAULT_WIDGET_HEIGHT,
    };
  },
);

export const getShareModalTopPosition = videoIndex =>
  GRID_HEADER_HEIGHT +
  videoIndex * (GRID_VIDEO_HEIGHT + GRID_VIDEO_MARGIN_BOTTOM) +
  GRID_VIDEO_HEIGHT / 2;
